import React, { useCallback, useRef, useState } from 'react';

import { JourneyMenu, JourneyMenuPropsInterface } from 'src/common/JourneyMenu';
import { EditorConfirmPanel } from 'src/editor/confirm-panel';
import { DeleteRegularIcon, FrameImageRegularIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';

import { Nullable } from 'src/types/nullable.type';
import LinkShareThumbnailPanel from './share-thumbnail-panel';

type ShareLinkItemMenuPanelType = 'thumbnail' | 'delete';

interface Props extends Pick<JourneyMenuPropsInterface, 'renderOrigin' | 'placement'> {
  onMenuClose: () => void;
  onMenuOpen: () => void;
  onCopyThumbnailClick: () => void;
  onDeleteConfirm: () => void;
}

export const ShareAdditionalLinkItemMenu = ({
  renderOrigin,
  placement,
  onMenuOpen,
  onMenuClose,
  onCopyThumbnailClick,
  onDeleteConfirm,
}: Props) => {
  const [panelType, setPanelType] = useState<Nullable<ShareLinkItemMenuPanelType>>(null);
  const settingsMenuOpenRef = useRef(functionNoop);
  const settingsMenuCloseRef = useRef(functionNoop);

  const deletePanelOpenRef = useRef(functionNoop);
  const deletePanelCloseRef = useRef(functionNoop);

  const thumbnailPanelOpenRef = useRef(functionNoop);
  const thumbnailPanelCloseRef = useRef(functionNoop);

  const onDeleteClicked = () => setPanelType('delete');

  const onMenuOpened = useCallback(() => {
    settingsMenuOpenRef.current();
    onMenuOpen();
  }, [onMenuOpen]);

  const onDeletePanelClosed = () => {
    setPanelType(null);
    deletePanelCloseRef.current();
    onMenuClose();
  };

  const onThumbnailPanelClosed = () => {
    setPanelType(null);
    thumbnailPanelCloseRef.current();
    onMenuClose();
  };

  const onBackClicked = () => {
    onDeletePanelClosed();
    setTimeout(onMenuOpened, 50);
  };

  const onConfirmClicked = () => {
    onDeleteConfirm();
    onDeletePanelClosed();
  };

  const onCopyThumbnailLinkClicked = () => {
    setPanelType('thumbnail');
    onCopyThumbnailClick();
  };

  const onMenuClosed = () => {
    onMenuClose();
  };

  if (panelType === 'delete') {
    return (
      <EditorConfirmPanel
        renderOrigin={renderOrigin}
        placement={placement}
        openRef={deletePanelOpenRef}
        closeRef={deletePanelCloseRef}
        copy='Deleting this link is an irreversible action'
        confirmCopy='Yes, delete'
        denyCopy='No, keep'
        onOpen={onMenuOpen}
        onClose={onMenuClose}
        onConfirm={onConfirmClicked}
        onDeny={onDeletePanelClosed}
        onBack={onBackClicked}
      />
    );
  }

  if (panelType === 'thumbnail') {
    return (
      <LinkShareThumbnailPanel
        renderOrigin={renderOrigin}
        placement={placement}
        openRef={thumbnailPanelOpenRef}
        closeRef={thumbnailPanelCloseRef}
        onOpen={onMenuOpen}
        onClose={onThumbnailPanelClosed}
        onCopy={onThumbnailPanelClosed}
        onBack={onBackClicked}
      />
    );
  }

  return (
    <JourneyMenu
      openRef={settingsMenuOpenRef}
      closeRef={settingsMenuCloseRef}
      placement={placement}
      onOpen={onMenuOpen}
      onClose={onMenuClosed}
      renderOrigin={renderOrigin}
    >
      <JourneyMenu.Item
        className='text-bedrock-p text-bedrock-black flex items-center space-x-sm'
        onClick={onCopyThumbnailLinkClicked}
      >
        <FrameImageRegularIcon />
        <span>Copy thumbnail link</span>
      </JourneyMenu.Item>

      <JourneyMenu.Item
        className='text-bedrock-p text-bedrock-black flex items-center space-x-sm'
        onClick={onDeleteClicked}
      >
        <DeleteRegularIcon />
        <span>Delete</span>
      </JourneyMenu.Item>
    </JourneyMenu>
  );
};
